import { File, Upload, X } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'

import { Button } from '@/ui-components/Button'
import { Heading } from '@/ui-components/Heading'

interface FileUploadZoneProps {
  selectedFiles: File[]
  onFilesChange: (files: File[]) => void
  onUpload: (files: File[]) => void
  isUploading?: boolean
  allowedFileTypes: { mimeType: string; extension: string }[]
  maxFileSize?: number
  getFileSize: (file: File) => string
}

export const FileUploadZone = ({
  selectedFiles,
  onFilesChange,
  onUpload,
  isUploading = false,
  allowedFileTypes,
  maxFileSize = 10 * 1024 * 1024, // 10MB default
  getFileSize,
}: FileUploadZoneProps) => {
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const allowedFileExtensions = Array.from(new Set(allowedFileTypes.map((type) => `.${type.extension}`))).join(',')

  const allowedMimeTypes = Array.from(new Set(allowedFileTypes.map((type) => type.mimeType)))

  const validateFile = useCallback(
    (file: File) => {
      // validate file size
      const isValidSize = file.size <= maxFileSize
      // validate file mime type
      const isValidMimeType = allowedMimeTypes.includes(file.type)
      // validate file is not a duplicate
      const isDuplicate = selectedFiles.some(
        (selectedFile) =>
          selectedFile.name === file.name && selectedFile.type === file.type && selectedFile.size === file.size,
      )

      return isValidSize && isValidMimeType && !isDuplicate
    },
    [maxFileSize, allowedMimeTypes, selectedFiles],
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleFileChange = useCallback(
    (files: File[]) => {
      // filter out files that are not valid
      const validFiles = files.filter(validateFile)

      if (validFiles.length > 0) {
        onFilesChange([...selectedFiles, ...validFiles])
      }
    },
    [selectedFiles, onFilesChange, validateFile],
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)

      const files = Array.from(e.dataTransfer.files)
      handleFileChange(files)
    },
    [handleFileChange],
  )

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        const files = Array.from(e.target.files)
        handleFileChange(files)
      }
    },
    [handleFileChange],
  )

  const removeFile = useCallback(
    (index: number) => {
      const newFiles = selectedFiles.filter((_, i) => i !== index)
      onFilesChange(newFiles)
    },
    [selectedFiles, onFilesChange],
  )

  const clearAllFiles = useCallback(() => {
    onFilesChange([])
    setIsDragging(false)
  }, [onFilesChange])

  const handleUpload = useCallback(() => {
    onUpload(selectedFiles)
  }, [selectedFiles, onUpload])

  const maxFileSizeMB = Math.round(maxFileSize / (1024 * 1024))

  return (
    <div className="space-y-6">
      {/* Drag and Drop Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          isDragging
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
            : 'border-gray-300 dark:border-gray-600'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Upload
          className="mx-auto size-12 text-gray-400 dark:text-gray-500 mb-4"
          data-testid="upload-icon"
        />
        <p className="text-gray-600 dark:text-gray-300 mb-2">Drag and drop files here, or</p>
        <Button
          color="primary"
          onClick={() => fileInputRef.current?.click()}
          className="mb-2"
        >
          Add Files
        </Button>
        <p className="text-sm text-gray-500 dark:text-gray-400">Maximum file size: {maxFileSizeMB} MB</p>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileSelect}
          accept={allowedFileExtensions}
          multiple
          data-testid="file-input"
        />
      </div>

      {/* Selected Files List */}
      {selectedFiles.length > 0 && (
        <div className="space-y-2">
          <Heading level={4}>Selected Files:</Heading>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {selectedFiles.map((selectedFile, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center gap-2">
                  <File className="size-4 text-gray-500 dark:text-gray-400" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{selectedFile.name}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">({getFileSize(selectedFile)})</span>
                </div>
                <Button
                  plain
                  onClick={() => removeFile(index)}
                >
                  <X data-slot="icon" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Actions */}
      {selectedFiles.length > 0 && (
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            className="disabled:opacity-50 disabled:cursor-not-allowed"
            plain
            onClick={clearAllFiles}
            disabled={isUploading}
          >
            Clear All
          </Button>
          <Button
            className="disabled:opacity-50 disabled:cursor-not-allowed"
            color="primary"
            onClick={handleUpload}
            disabled={isUploading}
            data-testid="upload-button"
          >
            {isUploading ? 'Uploading...' : 'Upload Files'}
          </Button>
        </div>
      )}
    </div>
  )
}
